import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import UserContext from "../../components/User_Context.js";
import * as menu from "../../assets/menus/menu.js";
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import { useTranslation } from "react-i18next";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { LisLabels } from "../_label/lis_labels/lis_labels.js";
import LisVocabularyToolbar from "../darm/components/lis/LisVocabularyToolbar.js";
import LisVocabularyTable from "../darm/components/lis/LisVocabularyTable.js";
import * as LisSwalModals from "../darm/components/lis/LisSwalModals.js";

const LisVocManagementScreen = () => {
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const MySwal = withReactContent(Swal);

  // Initialize i18n labels from centralized file
  const i18n = menu.getI18n();
  i18n.addResourceBundle("it", "lisLabels", LisLabels.it, true, true);
  i18n.addResourceBundle("en", "lisLabels", LisLabels.en, true, true);

  // Vocabulary configuration
  const vocabularies = useMemo(
    () => ({
      antibiotics: {
        name: "VOC_AWARE_ANTIBIOTICS",
        displayNameKey: "antibioticsVoc",
        columns: ["ATC_Code", "Antibiotic", "Class", "Category", "ListedEML"],
        pkField: "Id",
      },
      materials: {
        name: "VOC_AWARE_MATERIALS",
        displayNameKey: "materialsVoc",
        columns: ["ma_code", "maName"],
        pkField: "Id",
      },
      microorganisms: {
        name: "VOC_AWARE_MICROORGANISM",
        displayNameKey: "microorganismsVoc",
        columns: [
          "moCode",
          "moGroup",
          "moMicroorganism",
          "moMinimalList",
        ],
        pkField: "Id",
      },
      antibiotics_map: {
        name: "LIS_ANTIBIOTICS_MAP",
        displayNameKey: "antibioticsMapping",
        columns: ["Source_Text", "ATC_Code"],
        pkField: "Id",
      },
      materials_map: {
        name: "LIS_MATERIALS_MAP",
        displayNameKey: "materialsMapping",
        columns: ["Source_Text", "ma_code"],
        pkField: "Id",
      },
      microorganisms_map: {
        name: "LIS_MICROORGANISM_MAP",
        displayNameKey: "microorganismsMapping",
        columns: ["Source_Text", "moCode"],
        pkField: "Id",
      },
      provenienza: {
        name: "VOC_AWARE_PROVENIENZA",
        displayNameKey: "provenienzaVoc",
        columns: ["prov_description", "prov_code"],
        pkField: "Id",
      },
      analisi: {
        name: "VOC_AWARE_ANALISI",
        displayNameKey: "analisiVoc",
        columns: ["analisi_code", "analisi_description"],
        pkField: "Id",
      },
      alert_rules: {
        name: "LIS_ALERT_RULES",
        displayNameKey: "alertRulesManagement",
        columns: [
          "moGroup",
          "ATC_Code",
          "Mechanism",
          "Category",
          "MIC",
          "Notes",
          "WARN_ENABLED",
          "ALERT_ENABLED",
        ],
        pkField: "Id",
        booleanFields: ["WARN_ENABLED", "ALERT_ENABLED"],
      },
    }),
    []
  );

  const vocabularyKeys = useMemo(
    () => Object.keys(vocabularies),
    [vocabularies]
  );

  // State management
  const [selectedVocabulary, setSelectedVocabulary] = useState(
    () => vocabularyKeys[0] || ""
  );
  const [vocabularyData, setVocabularyData] = useState([]);
  const [jobStatus, setJobStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  // Track unsaved changes (dirty or new rows)
  const hasUnsavedChanges = useMemo(
    () => vocabularyData.some((row) => row.isDirty || row.isNew),
    [vocabularyData]
  );

  // Warn on browser/tab close when there are unsaved changes in edit mode
  useEffect(() => {
    if (!(isEditing && hasUnsavedChanges)) return;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isEditing, hasUnsavedChanges]);

  useEffect(() => {
    if (!selectedVocabulary && vocabularyKeys.length) {
      setSelectedVocabulary(vocabularyKeys[0]);
    }
  }, [selectedVocabulary, vocabularyKeys]);

  useEffect(() => {
    setIsEditing(false);
  }, [selectedVocabulary]);

  // Fetch job status on mount and setup auto-refresh every 5 seconds
  useEffect(() => {
    // Set breadcrumb
    const path = [
      { link: "/dashboard", name: "Home" },
      { link: "/admin", name: "Admin" },
      {
        link: "/admin/lis_voc_management",
        name: "LIS Vocabulary Management",
      },
    ];
    breadcrumb.set(path);

    // Write menu to make it visible
    menu.write({ user: currentUser() });

    fetchJobStatus();
    const interval = setInterval(fetchJobStatus, 5000);

    return () => clearInterval(interval);
  }, []);

  // Fetch vocabulary data when selection changes
  useEffect(() => {
    if (selectedVocabulary) {
      fetchVocabularyData();
    }
  }, [selectedVocabulary]);

  // Fetch job status from LIS_JOB_STATUS table
  const fetchJobStatus = async () => {
    try {
      const response = await api.sendGetRequest(`admin/lis/status`, {
        user: currentUser(),
        silentMode: true,
      });
      if (response.success) {
        setJobStatus(response.data);
      }
    } catch (error) {
      console.error("Error fetching job status:", error);
    }
  };

  // Fetch vocabulary data
  const fetchVocabularyData = async () => {
    setLoading(true);
    try {
      const response = await api.sendGetRequest(
        `admin/lis/vocabulary/${selectedVocabulary}`,
        {
          user: currentUser(),
        }
      );
      if (response.success) {
        // Add stable _rowId to existing rows for React key stability
        const dataWithIds = (response.data || []).map((row, idx) => ({
          ...row,
          _rowId: row.Id || row[vocabularies[selectedVocabulary].pkField] || `row_${idx}`,
        }));
        setVocabularyData(dataWithIds);
      } else {
        MySwal.fire(
          LisSwalModals.fetchVocabularyDataSwal(response)
        );
      }
    } catch (error) {
      console.error("Error fetching vocabulary:", error);
      MySwal.fire(
        LisSwalModals.fetchVocabularyDataSwal(error)
      );
    } finally {
      setLoading(false);
    }
  };

  // Add new row
  const handleAddRow = () => {
    const vocab = vocabularies[selectedVocabulary];
    const newRow = {
      _rowId: `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Unique stable ID for React key
      ValidFrom: new Date().toISOString(),
      ValidTo: null,
      Source: "LOCAL",
      isNew: true,
    };

    vocab.columns.forEach((col) => {
      newRow[col] = "";
    });

    setVocabularyData([newRow, ...vocabularyData]);
  };

  // Helpers: validation for required fields
  const isEmptyValue = (value, isBooleanField = false) => {
    if (isBooleanField) {
      return value === undefined || value === null;
    }
    if (value === null || value === undefined) return true;
    if (typeof value === "string") return value.trim() === "";
    return false;
  };

  const getMissingRequiredFields = (row, vocab) => {
    const booleanSet = new Set(vocab.booleanFields || []);
    const cols = vocab.columns || [];
    const missing = [];
    for (const col of cols) {
      const isBoolean = booleanSet.has(col);
      if (isEmptyValue(row[col], isBoolean)) {
        missing.push(col);
      }
    }
    return missing;
  };

  // Handle field change
  const handleFieldChange = (index, field, value) => {
    const updatedData = [...vocabularyData];
    updatedData[index][field] = value;
    updatedData[index].isDirty = true;
    setVocabularyData(updatedData);
  };

  // Save single row
  const handleSaveRow = async (index) => {
    const row = vocabularyData[index];
    const vocab = vocabularies[selectedVocabulary];

    try {
      // Validate required fields for new rows
      if (row.isNew) {
        const missing = getMissingRequiredFields(row, vocab);
        if (missing.length > 0) {
          const missingList = missing
            .map((f) => `<li><code>${f}</code></li>`)
            .join("");
          MySwal.fire(
            LisSwalModals.handleSaveRowSwal(missingList)
          );
          return;
        }
      }

      let response;
      if (row.isNew) {
        response = await api.sendMessage(
          `admin/lis/vocabulary/${selectedVocabulary}`,
          {
            user: currentUser(),
            body: row,
          }
        );
      } else {
        response = await api.sendMessage(
          `admin/lis/vocabulary/${selectedVocabulary}/${row[vocab.pkField]
          }`,
          {
            user: currentUser(),
            body: row,
            method: "PUT",
          }
        );
      }

      if (response.success) {
        MySwal.fire(
          LisSwalModals.recordSavedSuccessSwal()
        );
        fetchVocabularyData();
      } else {
        MySwal.fire(
          LisSwalModals.recordSavedFailedSwal(response)
        );
      }
    } catch (error) {
      console.error("Error saving row:", error);
      MySwal.fire(
        LisSwalModals.recordSavedFailedSwal(error)
      );
    }
  };

  // Cancel editing and discard changes
  const handleCancelEdit = async () => {
    if (hasUnsavedChanges) {
      const result = await MySwal.fire(LisSwalModals.handleCancelEditSwal(t));



      if (!result.isConfirmed) {
        return;
      }

      // Refresh data to discard changes
      await fetchVocabularyData();
    }
    setIsEditing(false);
  };

  // Save all changes using bulk endpoint
  const handleSaveChanges = async () => {
    // Collect all modified or new records
    const modifiedRows = vocabularyData
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.isDirty || row.isNew);

    if (modifiedRows.length === 0) {
      MySwal.fire(
        LisSwalModals.handleSaveChangesSwal()
      );
      setIsEditing(false);
      return;
    }

    // Show confirmation
    const result = await MySwal.fire(
      LisSwalModals.handleSaveChangesConfirmSwal(modifiedRows)
    );

    if (!result.isConfirmed) {
      return;
    }

    // Client-side validation: ensure all required fields are filled
    const vocab = vocabularies[selectedVocabulary];
    const invalids = [];
    for (const { row, index } of modifiedRows) {
      const missing = getMissingRequiredFields(row, vocab);
      if (missing.length > 0) {
        invalids.push({
          record: index + 1,
          id: row[vocab.pkField] || "(new)",
          missing,
        });
      }
    }

    if (invalids.length > 0) {
      const errorRows = invalids
        .map(
          (it) => `
            <tr>
              <td class="text-center"><strong>${it.record}</strong></td>
              <td><code>${it.id}</code></td>
              <td class="text-danger small">${it.missing
              .map((m) => `<code>${m}</code>`)
              .join(", ")}</td>
            </tr>
          `
        )
        .join("");

      MySwal.fire(
        LisSwalModals.validationFailedErrorSwal(invalids, errorRows)
      );
      // Do not send bulk request if validation fails
      return;
    }

    setLoading(true);

    try {
      // Prepare records for bulk update
      const recordsToSave = modifiedRows.map(({ row: record }) => {
        // Remove internal flags and temporary IDs
        const { isDirty, isNew, _rowId, ...cleanRecord } = record;
        return cleanRecord;
      });

      // Call bulk endpoint
      const response = await api.sendMessage(
        `admin/lis/vocabulary/${selectedVocabulary}/bulk`,
        {
          user: currentUser(),
          body: { records: recordsToSave },
        }
      );

      if (response.success) {
        const { results } = response.data || response;

        // Check for partial failures
        if (results && results.failed > 0) {
          // Show warning with details
          const errorRows = results.errors
            .map(
              (err) => `
              <tr>
                <td class="text-center"><strong>${err.record}</strong></td>
                <td><code>${err.id}</code></td>
                <td class="text-danger small">${err.error}</td>
              </tr>
            `
            )
            .join("");

          MySwal.fire(
            LisSwalModals.bulkUpdateCompletedWithErrorsSwal(results, errorRows)
          );
        } else {
          // All succeeded
          MySwal.fire(
            LisSwalModals.bulkUpdateSuccessSwal(response, modifiedRows)
          );
        }

        // Refresh data and exit edit mode
        await fetchVocabularyData();
        setIsEditing(false);
      } else {
        // Handle failure response - check if there are detailed errors
        const { results, errors } = response.data || response;

        if (results && results.errors && results.errors.length > 0) {
          // Show detailed error breakdown
          const errorRows = results.errors
            .map(
              (err) => `
              <tr>
                <td class="text-center"><strong>${err.record}</strong></td>
                <td><code>${err.id || 'N/A'}</code></td>
                <td class="text-danger small">${err.error}</td>
              </tr>
            `
            )
            .join("");

          MySwal.fire(
            LisSwalModals.bulkUpdateFailedSwal(response, results, errorRows)
          );
        } else if (errors && Array.isArray(errors) && errors.length > 0) {
          // Handle validation errors (when no results object exists)
          const errorList = errors.map(err => `<li class="text-danger">${err}</li>`).join('');
          MySwal.fire(
            LisSwalModals.validationErrorNoObjectSwal(response, errorList)
          );
        } else {
          // Fallback to simple error message
          MySwal.fire(
            LisSwalModals.bulkUpdateFailedSwal(response,  )
          );
        }
      }
    } catch (error) {
      console.error("Error during bulk update:", error);
      MySwal.fire(
        LisSwalModals.bulkUpdateNetworkErrorSwal(error)
      );
    } finally {
      setLoading(false);
    }
  };

  // Delete row
  const handleDeleteRow = async (index) => {
    const row = vocabularyData[index];
    const vocab = vocabularies[selectedVocabulary];

    if (row.isNew) {
      const updatedData = vocabularyData.filter((_, i) => i !== index);
      setVocabularyData(updatedData);
      return;
    }

    const result = await MySwal.fire(
      LisSwalModals.deactivateRecordSwal()
    );

    if (result.isConfirmed) {
      try {
        const response = await api.sendMessage(
          `admin/lis/vocabulary/${selectedVocabulary}/${row[vocab.pkField]
          }/delete`,
          {
            user: currentUser(),
          }
        );
        if (response.success) {
            MySwal.fire(
              LisSwalModals.deactivateSuccessSwal()
            );
          fetchVocabularyData();
        } else {
          MySwal.fire(
            LisSwalModals.deactivateFailedSwal(response)
          );
        }
      } catch (error) {
        console.error("Error deleting row:", error);
        MySwal.fire(
          LisSwalModals.deactivateFailedSwal(error)
        );
      }
    }
  };

  // Handle Excel export
  const handleExportExcel = async () => {
    setLoading(true);
    try {
      const vocab = vocabularies[selectedVocabulary];
      const blob = await api.downloadFileGet(
        `admin/lis/vocabulary/${selectedVocabulary}/export`,
        {
          user: currentUser(),
        }
      );

      // Check if response is a valid blob (not an error JSON)
      if (blob && blob instanceof Blob && blob.type !== "application/json") {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${vocab.name}_${new Date().toISOString().split("T")[0]
          }.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        MySwal.fire({
          icon: "success",
          title: "Success",
          text: t("exportToExcel", { ns: "lisLabels" }) + " - OK",
          timer: 2000,
        });
      } else {
        // Try to parse error from blob if it's JSON
        let errorMessage = "Failed to export vocabulary";
        if (blob && blob instanceof Blob) {
          try {
            const text = await blob.text();
            const errorData = JSON.parse(text);
            errorMessage = errorData.message || errorMessage;
          } catch (e) {
            // Ignore parse errors
          }
        }
        MySwal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
        });
      }
    } catch (error) {
      console.error("Error exporting Excel:", error);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to export vocabulary",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Excel import
  const handleImportExcel = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setLoading(true);
      setUploadProgress(0);

      try {
        // Start upload session for LIS vocabulary
        const startResult = await api.sendMessage(
          "admin/lis/vocabulary/upload/start",
          {
            user: currentUser(),
            body: {
              filename: file.name,
              totalSize: file.size,
            },
          }
        );

        if (!startResult.success) {
          throw new Error(
            startResult.message || "Upload initialization failed"
          );
        }

        const fileUID = startResult.fileUID;
        const chunkSize = 1024 * 1024; // 1MB chunks
        let offset = 0;

        while (offset < file.size) {
          const chunk = file.slice(offset, offset + chunkSize);
          const chunk_array = await chunk.arrayBuffer();
          const chunk_data = Array.from(new Uint8Array(chunk_array));

          const chunkResult = await api.sendMessage(
            "admin/lis/vocabulary/upload/chunk",
            {
              user: currentUser(),
              body: {
                fileUID: fileUID,
                chunkData: chunk_data,
                offset: offset,
              },
            }
          );

          if (!chunkResult.success) {
            throw new Error(chunkResult.message || "Chunk upload failed");
          }

          offset += chunkSize;
          const progress = Math.min(
            Math.round((offset / file.size) * 100),
            100
          );
          setUploadProgress(progress);
        }

        // Call import API
        const response = await api.sendMessage(
          `admin/lis/vocabulary/${selectedVocabulary}/import`,
          {
            user: currentUser(),
            body: {
              fileUID: fileUID,
            },
          }
        );

        if (response.success) {
          // Check if there are errors to show
          const hasErrors =
            response.data.errors && response.data.errors.length > 0;

          // Normalize counters (backend may use 'imported' or 'inserted')
          const deactivatedCount = response.data.deactivated || 0;
          const importedCount =
            response.data.imported != null
              ? response.data.imported
              : response.data.inserted || 0;
          const updatedCount = response.data.updated || 0;
          const unchangedCount =
            response.data.unchanged != null ? response.data.unchanged : undefined;

          if (hasErrors) {
            // Show detailed error report
            const errorRows = response.data.errors
              .map((err) => {
                const errorMessages = Array.isArray(err.errors)
                  ? err.errors.join("; ")
                  : err.errors;
                return `
                                <tr>
                                    <td class="text-center"><strong>${err.row}</strong></td>
                                    <td class="text-danger small">${errorMessages}</td>
                                </tr>
                            `;
              })
              .join("");

            MySwal.fire(
              LisSwalModals.importCompletedWithErrorsSwal(deactivatedCount, importedCount, updatedCount, errorRows, response)
            );
          } else {
            // No errors - show simple success message
            MySwal.fire(
              LisSwalModals.importCompletedSuccessSwal(deactivatedCount, importedCount, updatedCount, unchangedCount)
            );
          }
          fetchVocabularyData();
        } else {
          MySwal.fire(
            LisSwalModals.importFailedSwal(response)
          );
        }
      } catch (error) {
        console.error("Error importing Excel:", error);
        MySwal.fire(
          LisSwalModals.importFailedSwal(error)
        );
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    };
    input.click();
  };

  // Components are rendered via  LisVocabularyToolbar, and LisVocabularyTable

  return (
    <>
      <div className="page-titles">
        <ol className="breadcrumb"></ol>
      </div>
      <div className="container-fluid">
        <div className="card-body p-0 bg-white  ">
          <div
            className="table-responsive active-projects task-table"
            style={{}}
          >
            <div className="tbl-caption h-auto">
              <div className="row">
                <div className="col-12  ">
                  <div className="col-12">
                    <h3 className="mb-0">
                      <i className="fas fa-book me-2"></i>
                      {t("lisAlertManagement", { ns: "lisLabels" })}
                    </h3>
                    <p className="text-muted">
                      {t("lisAlertManagementDescription", { ns: "lisLabels" })}
                    </p>
                  </div>
                </div>
              </div>
              <div >
                <LisVocabularyToolbar
                  t={t}
                  vocabularies={vocabularies}
                  selectedVocabulary={selectedVocabulary}
                  onChangeSelectedVocabulary={setSelectedVocabulary}
                  onAddRow={handleAddRow}
                  onImportExcel={handleImportExcel}
                  onExportExcel={handleExportExcel}
                  loading={loading}
                  isEditing={isEditing}
                />
                <LisVocabularyTable
                  t={t}
                  vocabularyData={vocabularyData}
                  columns={vocabularies[selectedVocabulary].columns}
                  pkField={vocabularies[selectedVocabulary].pkField}
                  booleanFields={vocabularies[selectedVocabulary].booleanFields || []}
                  vocabularies={vocabularies}
                  selectedVocabulary={selectedVocabulary}
                  onFieldChange={handleFieldChange}
                  onSaveRow={handleSaveRow}
                  onDeleteRow={handleDeleteRow}
                  onAddRow={handleAddRow}
                  isEditing={isEditing}
                  onToggleEdit={() => setIsEditing((prev) => !prev)}
                  onSaveChanges={handleSaveChanges}
                  onCancelEdit={handleCancelEdit}
                  hasUnsavedChanges={hasUnsavedChanges}
                  uploadProgress={uploadProgress}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LisVocManagementScreen;
